'use client';

import { type ButtonHTMLAttributes, type ReactNode, forwardRef, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Popover, closePopover, type PopoverProps } from './popover';

export interface MenuProps extends Omit<PopoverProps, 'children'> {
  children: ReactNode;
  label: string;
}

/**
 * Menu: a Popover whose panel is a `role=menu` with roving arrow-key focus.
 * Items are `MenuItem`s; each closes the menu after its action.
 */
export function Menu({ children, label, className, ...popover }: MenuProps) {
  const listRef = useRef<HTMLDivElement>(null);

  function items(): HTMLElement[] {
    return Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('[role=menuitem]:not([disabled])') ?? []
    );
  }

  return (
    <Popover
      {...popover}
      className={cn('py-1', className)}
      onOpenChange={(open) => {
        popover.onOpenChange?.(open);

        if (open) queueMicrotask(() => items()[0]?.focus());
      }}
    >
      <div
        ref={listRef}
        role="menu"
        aria-label={label}
        onKeyDown={(e) => {
          const list = items();
          // SAFETY: The surrounding typed contract establishes the asserted representation.
          const i = list.indexOf(document.activeElement as HTMLElement);

          if (e.key === 'ArrowDown') {
            e.preventDefault();
            list[(i + 1) % list.length]?.focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            list[(i - 1 + list.length) % list.length]?.focus();
          } else if (e.key === 'Home') {
            e.preventDefault();
            list[0]?.focus();
          } else if (e.key === 'End') {
            e.preventDefault();
            list[list.length - 1]?.focus();
          }
        }}
      >
        {children}
      </div>
    </Popover>
  );
}

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  tone?: 'default' | 'critical';
  /** Keep the menu open after click (rare: toggles). */
  keepOpen?: boolean;
}

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  ({ icon, tone = 'default', keepOpen, className, onClick, children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      role="menuitem"
      tabIndex={-1}
      onClick={(e) => {
        onClick?.(e);

        if (!keepOpen && !e.defaultPrevented) closePopover(e.currentTarget);
      }}
      className={cn(
        'flex w-full items-center gap-2 rounded-[6px] px-2.5 py-1.5 text-left text-sm',
        'focus-visible:outline-none focus-visible:bg-surface-2 hover:bg-surface-2',
        'disabled:pointer-events-none disabled:opacity-50',
        '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-ink-2',
        tone === 'critical' ? 'text-critical [&_svg]:text-critical' : 'text-ink',
        className
      )}
      {...props}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  )
);

MenuItem.displayName = 'MenuItem';

export function MenuSeparator() {
  return <div role="separator" className="my-1 h-px bg-line" />;
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-2.5 py-1.5 text-[11px] font-medium uppercase tracking-[0.08em] text-ink-3">
      {children}
    </div>
  );
}
